import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import type { Edge, Node } from '../../../core/models/scene';
import {
  getGravityUserId,
  getGravityUserOrgId,
  useAuthenticatedFetch,
} from '../../../hooks/useAuth';
import type { SuccessionCommandServerPort, UpdateSuccessionPlanParams } from '../commands';
import type {
  SuccessionPlanningProjection,
  SuccessionProjectionNomination,
  SuccessionProjectionSummary,
  SuccessionSlateFact,
} from '../models/SuccessionPlanningPersistence';
import type { PositionStaffingPersonRow } from '../models/PositionStaffingIntent';
import type {
  CanonicalSuccessionSlate,
  SuccessionCandidate,
  SuccessionCandidatePerson,
} from '../models/SuccessionPlanning';
import { resolveSuccessionTarget } from '../services/SuccessionPlanningService';
import { successionConnectorStyleAttrs } from '../services/successionLensEdges';
import {
  augmentSuccessionGraphWithRecordOccupancy,
  keyPositionByPositionRef,
  occupantNodeIdByPositionRef,
} from '../services/successionRecordOccupancy';
import { useOrgModel } from './useOrgModel';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { isSavedPeopleView } from '../models/orgArtifactKind';
import { orgPlanningEditingContextKey, useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import {
  successionOptimisticContext,
  successionOptimisticDraftContext,
  useSuccessionOptimisticSlateStore,
} from '../stores/successionOptimisticSlateStore';
import { useOrgPlanningScenarioProjection } from './useOrgPlanningScenarioProjection';
import { subscribeToSuccessionCarryComplete } from '../services/successionCarrySignal';
import {
  enqueueLatestSuccessionMutation,
  enqueueSuccessionTransition,
  acknowledgeFailedOrgPlanningMutation,
  orgPlanningCommandDeadlineSignal,
  registerOrgPlanningCommandBarrier,
} from './orgPlanningCommandSequencer';
import {
  captureProtectedPlanningGeneration,
  isProtectedPlanningGenerationCurrent,
  type ProtectedPlanningGeneration,
} from './protectedPlanningGeneration';
import { primeAcceptedSuccessionAccess } from './acceptedSuccessionAccess';
import { isSuccessionDraftConflict, type SuccessionDraftMutationState } from '../models/successionDraftConflict';
import { acknowledgeDraftHistoryRestore, draftHistoryEquivalent } from './orgPlanningDraftHistory';
import { captureOrgPlanningReviewScope, isOrgPlanningReviewScopeCurrent } from './useOrgPlanningReviewScope';
import { sameOrgPlanningReviewScope } from './orgPlanningReviewKey';
import { abandonAtlasDraftSave, assertAtlasDraftHistoryAllowed, completeAtlasDraftSave, recoverAtlasDraftSave, trackAtlasDraftSave, useAtlasDraftSaveRecoveryStore } from '../stores/atlasDraftSaveRecoveryStore';
import type { ICommand } from '../../../core/commands/ICommand';
import { PositionCommandSettlementError } from './positionCommandSettlement';
import { atlasReviewBlocksEditing } from '../stores/atlasChangesStore';
import type { ConfirmedKeyPositionPatch } from '../services/applyConfirmedSuccessionEnactment';

export const successionPlanningKeys = {
  projection: (folderId: string, diagramId: string, scenarioId?: string, headChangeSetId?: string) =>
    ['atlas-succession', folderId, diagramId, scenarioId, headChangeSetId] as const,
};

interface DraftHead {
  readonly changeSetId?: string;
  readonly scenarioId?: string;
  readonly awaitingProjection: boolean;
}

type SuccessionSaveTarget =
  | { readonly draftMode: 'create' }
  | {
      readonly draftMode: 'amend';
      readonly changeSetId: string;
      readonly scenarioId: string;
      readonly expectedHeadChangeSetId: string;
    };

export function useSuccessionPlanningProjection(input: {
  folderId?: string | null;
  diagramId?: string | null;
  nodes: Node[];
  edges: Edge[];
  enabled: boolean;
  /** Canonical, permissioned People returned by the shared Atlas search. */
  candidatePeople?: readonly PositionStaffingPersonRow[];
  /**
   * Project durable position records (virtual Position nodes + occupies
   * edges) into the succession graph for record-backed people. Panel-only:
   * canvas consumers render the projection verbatim and must never receive
   * virtual nodes.
   */
  projectRecordOccupancy?: boolean;
}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const isServerBacked = Boolean(folderId && diagramId && diagramId !== 'test');
  const storedScenario = useOrgPlanningScenarioStore((state) => state.selectedByDiagram[diagramId]);
  const editingContext = useOrgPlanningScenarioStore((state) => orgPlanningEditingContextKey(diagramId, state));
  // Share the canvas/panel overlay only within this selected plan. Failed
  // input stays in its original context when the user visits another plan.
  const commandContext = successionOptimisticContext(folderId, diagramId);
  const optimisticContext = `${commandContext}|${encodeURIComponent(editingContext)}`;
  const storedOptimisticSlates = useSuccessionOptimisticSlateStore(
    (state) => state.slatesByContext[optimisticContext],
  );
  const draftHeadRef = useRef<DraftHead>({ awaitingProjection: false });
  const draftContextRef = useRef(optimisticContext);
  // A saved view reads its source chart's applied succession only; it never
  // discovers, selects, or projects organization-plan drafts.
  const isSavedView = isSavedPeopleView(useDiagramInfo(diagramId || undefined).data?.viewDefinition);
  const sharedScenarioProjection = useOrgPlanningScenarioProjection({
    folderId,
    diagramId,
    projectionKind: 'people',
    enabled: input.enabled && isServerBacked && !isSavedView,
  });
  const isLiveProjection = isSavedView || sharedScenarioProjection.mode === 'live';
  const selectedScenario = isLiveProjection ? undefined : storedScenario;
  const optimisticSlates = isLiveProjection ? undefined : storedOptimisticSlates;

  const query = useQuery<SuccessionPlanningProjection>({
    queryKey: [...successionPlanningKeys.projection(
      folderId,
      diagramId,
      selectedScenario?.scenarioId,
      selectedScenario?.headChangeSetId,
    ), ...(isLiveProjection ? ['live'] : [])],
    enabled: input.enabled && isServerBacked,
    queryFn: async ({ signal }) => {
      const response = await authenticatedFetch(
        `/api/folders/${folderId}/diagrams/${diagramId}/atlas/succession${selectedScenario?.headChangeSetId
          ? `?changeSetId=${encodeURIComponent(selectedScenario.headChangeSetId)}`
          : ''}`,
        { signal },
      );
      if (!response.ok) throw new Error(await apiError(response, 'Failed to load succession plan'));
      return response.json();
    },
    // Draft saves advance the immutable change-set head. Keep the last
    // authorized projection usable while the new head loads so access gating
    // does not disable the controlled readiness inputs between amendments.
    // The optimistic slate below replaces the stale target facts meanwhile.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === folderId
        && previousQuery.queryKey[2] === diagramId
        && (previousQuery.queryKey[3] === selectedScenario?.scenarioId
          || Boolean(selectedScenario && previous?.changeSet?.id === selectedScenario.headChangeSetId
            && previous.changeSet.scenarioId === selectedScenario.scenarioId))
        && (previousQuery.queryKey[5] === 'live') === isLiveProjection ? previous : undefined,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!isServerBacked) return undefined;
    return subscribeToSuccessionCarryComplete(folderId, diagramId, () => {
      void invalidateSuccessionDraftQueries(queryClient, folderId, diagramId);
    });
  }, [diagramId, folderId, isServerBacked, queryClient]);

  useEffect(() => {
    if (draftContextRef.current === optimisticContext) return;
    draftContextRef.current = optimisticContext;
    draftHeadRef.current = { awaitingProjection: false };
  }, [optimisticContext]);

  useEffect(() => {
    const projectedHead = query.data?.changeSet?.id;
    const selectedHead = selectedScenario?.headChangeSetId;
    const current = draftHeadRef.current;
    if (current.awaitingProjection && projectedHead === current.changeSetId) {
      draftHeadRef.current = {
        changeSetId: projectedHead,
        scenarioId: query.data?.changeSet?.scenarioId ?? selectedScenario?.scenarioId,
        awaitingProjection: false,
      };
    } else if (!current.awaitingProjection) {
      const remoteHead = selectedHead ?? projectedHead;
      if (remoteHead) {
        draftHeadRef.current = {
          changeSetId: remoteHead,
          scenarioId: selectedScenario?.scenarioId ?? query.data?.changeSet?.scenarioId,
          awaitingProjection: false,
        };
      }
    }

    if (!query.data || query.isPlaceholderData
      || query.data.changeSet?.id !== selectedScenario?.headChangeSetId) return;
    const projected = query.data;
    useSuccessionOptimisticSlateStore.getState().settle(
      optimisticContext,
      (slate, targetRef) => sameSuccessionSlate(slate, slateFromProjection(projected, targetRef)),
    );
  }, [optimisticContext, query.data, query.isPlaceholderData, selectedScenario?.headChangeSetId, selectedScenario?.scenarioId]);

  const save = useMutation({
    mutationFn: async (body: {
      intentId: string;
      slate: SuccessionSlateFact;
      signal?: AbortSignal;
      generation: ProtectedPlanningGeneration;
      editEpoch: number;
      editingContext: string;
    } & SuccessionSaveTarget) => {
      const { signal, generation, editEpoch: _editEpoch, editingContext: _editingContext, ...payload } = body;
      if (!isProtectedPlanningGenerationCurrent(generation)) {
        throw new DOMException('Protected planning access changed.', 'AbortError');
      }
      const deadline = orgPlanningCommandDeadlineSignal(signal);
      let reconcile = false;
      try {
        const response = await authenticatedFetch(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/succession/change-set`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: deadline.signal,
        });
        if (response.status === 409) {
          const conflict = await response.clone().json().catch(() => null) as { error?: string } | null;
          reconcile = conflict?.error === 'intent_in_progress';
        }
        if (!reconcile) {
          if (!response.ok) throw new PositionCommandSettlementError('http_error', response.status,
            await apiError(response, 'Failed to save succession plan'));
          return response.json() as Promise<{
            changeSetId: string;
            scenarioId: string;
            mayApply?: boolean;
          }>;
        }
      } catch (error) {
        if (deadline.timedOut() && !signal?.aborted) reconcile = true;
        else throw error;
      } finally {
        deadline.dispose();
      }
      if (!reconcile) throw new Error('The succession save outcome could not be verified.');
      for (let attempt = 0; attempt < 6; attempt += 1) {
        await waitForSuccessionIntent(250 * (attempt + 1), signal);
        const response = await authenticatedFetch(
          `/api/folders/${folderId}/diagrams/${diagramId}/atlas/succession/intents/${payload.intentId}`,
          { signal },
        );
        if (!response.ok) throw new Error(await apiError(response, 'Failed to reconcile succession save'));
        const status = await response.json() as
          | { state: 'accepted'; changeSetId: string; scenarioId: string }
          | { state: 'unknown' };
        if (status.state === 'accepted') {
          return { changeSetId: status.changeSetId, scenarioId: status.scenarioId };
        }
      }
      throw new Error('The succession save outcome is unknown. Refresh before retrying.');
    },
    onSuccess: (result, variables) => {
      if (!isProtectedPlanningGenerationCurrent(variables.generation)) return;
      useOrgPlanningScenarioStore.getState().bindEditingContext(diagramId, result.scenarioId, variables.editingContext);
      if (!useOrgPlanningScenarioStore.getState().adopt(diagramId, {
        scenarioId: result.scenarioId, headChangeSetId: result.changeSetId,
      }, variables.editEpoch)) {
        void invalidateSuccessionDraftQueries(queryClient, folderId, diagramId).catch(() => undefined);
        return;
      }
      draftHeadRef.current = {
        scenarioId: result.scenarioId,
        changeSetId: result.changeSetId,
        awaitingProjection: true,
      };
      // The mutation response is the authoritative acceptance of this immutable
      // head. Publish that lifecycle immediately under the next query key so
      // the panel can expose Apply and begin its exact draft-access check
      // without waiting for a second network round trip. Protected slate values
      // remain in the dedicated optimistic store; none enter Yjs.
      const acceptedProjection = projectionWithAcceptedDraft(
        query.data,
        result,
        variables.slate,
      );
      if (acceptedProjection) {
        queryClient.setQueryData(
          successionPlanningKeys.projection(
            folderId,
            diagramId,
            result.scenarioId,
            result.changeSetId,
          ),
          acceptedProjection,
        );
      }
      primeAcceptedSuccessionAccess({
        queryClient,
        principalId: getGravityUserId(),
        actorOrgId: getGravityUserOrgId(),
        folderId,
        diagramId,
        changeSetId: result.changeSetId,
        mayApply: result.mayApply,
      });
      // Server acceptance and shared-head advancement complete the command.
      // Reconciliation is deliberately background work: awaiting invalidation
      // here made a discrete readiness selection feel as slow as the refetch.
      void invalidateSuccessionDraftQueries(queryClient, folderId, diagramId)
        .catch(() => undefined);
    },
  });

  const draftHistory = useMutation({
    mutationFn: async (input: { sourceHead: string; restoreHead?: string; scenarioId: string; signal: AbortSignal }) => {
      const deadline = orgPlanningCommandDeadlineSignal(input.signal);
      try {
        const response = await authenticatedFetch(
          `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${input.sourceHead}/${input.restoreHead ? 'restore-previous-draft-revision' : 'discard'}`,
          { method: 'POST', signal: deadline.signal, ...(input.restoreHead ? {
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restoreChangeSetId: input.restoreHead }),
          } : {}) });
        if (!response.ok) throw new PositionCommandSettlementError('http_error', response.status,
          await apiError(response, 'Could not restore the saved draft revision'));
        const receipt = await response.json() as { id?: string; scenarioId?: string; ownerDiagramId?: string };
        if (!receipt.id || receipt.scenarioId !== input.scenarioId || receipt.ownerDiagramId !== diagramId
          || (!input.restoreHead && receipt.id !== input.sourceHead)) throw new Error('The draft history receipt did not match.');
        return receipt.id;
      } finally { deadline.dispose(); }
    },
  });

  const transition = useMutation({
    mutationFn: async (request: { changeSetId: string; action: 'apply' | 'revert' | 'discard' | 'restore'; signal?: AbortSignal }) => {
      const deadline = orgPlanningCommandDeadlineSignal(request.signal);
      try {
        const response = await authenticatedFetch(
          request.action === 'apply'
            ? `/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-changes/${request.changeSetId}/apply`
            : `/api/folders/${folderId}/diagrams/${diagramId}/atlas/succession/change-sets/${request.changeSetId}/${request.action}`,
          { method: 'POST', signal: deadline.signal },
        );
        if (!response.ok) throw new Error(await apiError(response, `Failed to ${request.action} succession plan`));
        const result = await response.json() as { changeSetId?: string; graphVersion?: number };
        if (!result.changeSetId || !Number.isFinite(result.graphVersion)) {
          throw new Error('The server returned an incomplete succession transition receipt.');
        }
        return { changeSetId: result.changeSetId, graphVersion: result.graphVersion as number };
      } catch (error) {
        if (deadline.timedOut()) {
          throw new Error('The server did not confirm the transition in time. Its outcome is unknown; refresh the draft before retrying.');
        }
        throw error;
      } finally {
        deadline.dispose();
      }
    },
  });

  // Person charts rarely carry canvas Position nodes; project the durable
  // position records (and their occupancy) into the succession graph so
  // record-backed people resolve to a record-backed Position target.
  const projectRecordOccupancy = Boolean(input.projectRecordOccupancy);
  // Canvas consumers need the same occupancy to draw succession connectors
  // against the person holding the role, so the model is fetched whenever the
  // diagram is server-backed. It is one folder-scoped query, shared with the
  // property panel through the query cache.
  const orgModel = useOrgModel({
    folderId: input.folderId,
    enabled: input.enabled && isServerBacked,
  });
  const activeModel = sharedScenarioProjection.data?.model ?? orgModel.data;
  const candidatePeople = useMemo(() => {
    const byId = new Map<string, SuccessionCandidatePerson>();
    for (const person of activeModel?.people ?? []) {
      byId.set(person.id, {
        personId: person.id,
        displayLabel: person.label,
        title: readProjectedTitle(person.props),
      });
    }
    for (const person of input.candidatePeople ?? []) {
      const existing = byId.get(person.personId);
      byId.set(person.personId, {
        personId: person.personId,
        displayLabel: person.displayLabel,
        ...(existing?.title ? { title: existing.title } : {}),
      });
    }
    return [...byId.values()];
  }, [activeModel?.people, input.candidatePeople]);
  const candidateRefById = useMemo(
    () => new Map(candidatePeople.map((person) => [person.personId, `record:${person.personId}`])),
    [candidatePeople],
  );
  const canonicalRiskByRecordId = useMemo(() => new Map(
    [...(activeModel?.people ?? []), ...(activeModel?.positions ?? [])].map((entity) => [
      entity.id,
      {
        impactOfLoss: entity.props.impactOfLoss,
        flightRisk: entity.props.flightRisk,
        departureDate: entity.props.departureDate,
        retirementDate: entity.props.retirementDate,
      },
    ] as const),
  ), [activeModel?.people, activeModel?.positions]);
  const augmented = useMemo(
    () => augmentSuccessionGraphWithRecordOccupancy(
      input.nodes,
      input.edges,
      isServerBacked && projectRecordOccupancy ? activeModel : undefined,
    ),
    [
      activeModel,
      input.edges,
      input.nodes,
      isServerBacked,
      projectRecordOccupancy,
    ],
  );

  const canonicalSlates = useMemo(() => {
    const slates = new Map<string, CanonicalSuccessionSlate>();
    const targetRefs = new Set([
      ...(query.data?.summaries.map((summary) => summary.targetRef) ?? []),
      ...(query.data?.nominations.map((nomination) => nomination.targetRef) ?? []),
    ]);
    for (const targetRef of targetRefs) {
      slates.set(stripRecordReference(targetRef), canonicalSlateFromFact(
        slateFromProjection(query.data, targetRef),
      ));
    }
    for (const slate of Object.values(optimisticSlates ?? {}).map((entry) => entry.slate)) {
      slates.set(stripRecordReference(slate.targetRef), canonicalSlateFromFact(slate));
    }
    return slates;
  }, [optimisticSlates, query.data]);

  const occupantNodeIdByRef = useMemo(
    () => occupantNodeIdByPositionRef(
      augmented.nodes,
      isServerBacked ? sharedScenarioProjection.data?.model ?? orgModel.data : undefined,
    ),
    [augmented.nodes, isServerBacked, orgModel.data, sharedScenarioProjection.data?.model],
  );
  const canonicalKeyPositionByRef = useMemo(
    () => keyPositionByPositionRef(isServerBacked ? activeModel : undefined),
    [activeModel, isServerBacked],
  );

  const projection = useMemo(() => projectSuccessionPlanningOntoCanvas(
    augmented.nodes,
    augmented.edges,
    diagramId,
    isServerBacked ? query.data : undefined,
    isServerBacked,
    new Map(Object.entries(optimisticSlates ?? {}).map(([targetRef, optimistic]) => [targetRef, optimistic.slate])),
    occupantNodeIdByRef,
    canonicalKeyPositionByRef,
  ), [
    augmented.edges,
    augmented.nodes,
    diagramId,
    isServerBacked,
    canonicalKeyPositionByRef,
    occupantNodeIdByRef,
    optimisticSlates,
    query.data,
  ]);

  const createDraftPort = useCallback((params: Omit<UpdateSuccessionPlanParams, 'access' | 'serverPort'>): SuccessionCommandServerPort | undefined => {
    if (!isServerBacked) return undefined;
    const targetRef = resolveSlateTargetRef(params, projection.nodes, projection.edges, diagramId, query.data?.nominations);
    if (!targetRef) return undefined;
    const nextSlate = slateFromParams(params, targetRef, projection.nodes, diagramId, candidateRefById);
    const generation = captureProtectedPlanningGeneration(folderId);
    const historyScope = captureOrgPlanningReviewScope(folderId, diagramId);
    if (atlasReviewBlocksEditing(historyScope)) {
      toast.error('Return to editing from the pending review before making another change.');
      return undefined;
    }
    const selection = useOrgPlanningScenarioStore.getState();
    const startsFromLive = (selection.modeByDiagram[diagramId] ?? sharedScenarioProjection.mode) === 'live';
    const editEpoch = selection.beginEditing(diagramId, sharedScenarioProjection.mode);
    const capturedEditingContext = orgPlanningEditingContextKey(diagramId);
    const draftContext = successionOptimisticDraftContext(folderId, diagramId);
    const retainedInput = useSuccessionOptimisticSlateStore.getState().peek(draftContext)[targetRef];
    if (retainedInput?.phase === 'unsaved' && isSuccessionDraftConflict(retainedInput.error)) {
      toast.error('Review the current plan before replacing this unsaved edit.');
      return undefined;
    }
    const recoveryGroup = `${draftContext}|${targetRef}`;
    const earlierAttempt = Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).find((attempt) =>
      attempt.groupKey === recoveryGroup && sameOrgPlanningReviewScope(attempt.scope, historyScope)
      && attempt.phase !== 'saving');
    if (earlierAttempt) {
      if (retainedInput && sameSuccessionSlate(nextSlate, retainedInput.slate)) {
        const command = recoverAtlasDraftSave(earlierAttempt.id);
        return command ? { historyRecoveryId: command.historyRecoveryId, execute: async () => { await command.execute(); },
          undo: async () => { await command.undo(); }, redo: async () => { await command.redo(); } } : undefined;
      }
      toast.error('Resolve the earlier unsaved edit before making a new choice.');
      return undefined;
    }
    if (Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).some((attempt) =>
      attempt.phase !== 'saving' && sameOrgPlanningReviewScope(attempt.scope, historyScope))) {
      toast.error('Resolve the earlier unsaved edit before adding another change.');
      return undefined;
    }
    const previousSlate = retainedInput?.slate
      ?? slateFromProjection(query.data, targetRef);
    let conflictDetected = false;
    const stageSlate = (slate: SuccessionSlateFact): number => {
      if (conflictDetected) throw new Error('The plan changed since this edit. Review the current plan before making a new choice.');
      if ((useOrgPlanningScenarioStore.getState().editEpochByDiagram[diagramId] ?? 0) !== editEpoch) {
        throw new Error('The selected plan changed. Return to the original plan before undoing or redoing this edit.');
      }
      return useSuccessionOptimisticSlateStore.getState().stage(draftContext, slate);
    };
    const startingSelection = useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId];
    const startingTarget = startsFromLive
      ? { draftMode: 'create' as const }
      : successionSaveTarget(
          startingSelection?.headChangeSetId ?? query.data?.changeSet?.id,
          startingSelection?.scenarioId ?? query.data?.changeSet?.scenarioId,
        );
    if (isLiveProjection) {
      // Returning to Live means starting a new plan. Do not let the head from
      // a previously viewed scenario leak into this command after the mode
      // switch; accepted heads from this new command repopulate the ref.
      draftHeadRef.current = { awaitingProjection: false };
    }
    const forwardRevision = stageSlate(nextSlate);
    const intentId = crypto.randomUUID();
    const attemptedTargets = new Map<string, SuccessionSaveTarget>();
    let lastAcceptedHead: string | undefined;
    let forwardHead: string | undefined;
    let forwardBaseHead: string | undefined;
    let acceptedScenarioId: string | undefined;
    let emptiedHead: string | undefined;
    let undone = false;
    let historyAttempt: { action: 'undo' | 'redo'; revision: number;
      request?: { sourceHead: string; restoreHead?: string; scenarioId: string; predecessor?: string } } | undefined;
    const persist = async (
      slate: SuccessionSlateFact,
      rollbackSlate: SuccessionSlateFact,
      revision: number,
      requestIntentId: string,
    ) => {
      if (conflictDetected) throw new Error('The plan changed since this edit. Review the current plan before making a new choice.');
      try {
        await enqueueLatestSuccessionMutation(commandContext, `${draftContext}|${targetRef}`, async (signal) => {
          if (atlasReviewBlocksEditing(historyScope)) throw new Error('Return to editing from the pending review before making another change.');
          if (Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).some((attempt) => attempt.historyAction)) {
            assertAtlasDraftHistoryAllowed(historyScope, requestIntentId);
          }
          // Re-read after waiting for another mutation. An accepted create or
          // amendment may have advanced this exact diagram's head while the
          // command was queued.
          const queuedSelection = useOrgPlanningScenarioStore.getState();
          if ((queuedSelection.editEpochByDiagram[diagramId] ?? 0) !== editEpoch && !attemptedTargets.has(requestIntentId)) {
            throw new Error('The selected plan changed. Review this unsaved edit before saving it.');
          }
          const queuedHead = queuedSelection.selectedByDiagram[diagramId];
          if (!isOrgPlanningReviewScopeCurrent(historyScope)) throw new Error('Protected planning access changed.');
          const queuedTarget = attemptedTargets.get(requestIntentId) ?? (queuedHead
            ? successionSaveTarget(queuedHead.headChangeSetId, queuedHead.scenarioId)
            : startingTarget);
          attemptedTargets.set(requestIntentId, queuedTarget);
          const saveAgainst = (target: SuccessionSaveTarget) => save.mutateAsync({
            intentId: requestIntentId,
            ...target,
            slate,
            signal,
            generation,
            editEpoch,
            editingContext: capturedEditingContext,
          });
          // A refreshed head is not permission to replace another planner's
          // complete slate. Keep conflicts as unsaved input for explicit review.
          const accepted = await saveAgainst(queuedTarget);
          lastAcceptedHead = accepted.changeSetId;
          if (!forwardHead) {
            forwardHead = accepted.changeSetId;
            forwardBaseHead = queuedTarget.draftMode === 'amend' ? queuedTarget.changeSetId : undefined;
            acceptedScenarioId = accepted.scenarioId;
          }
        }, queryClient);
        if (isProtectedPlanningGenerationCurrent(generation)) {
          useSuccessionOptimisticSlateStore.getState().accept(
            draftContext,
            targetRef,
            revision,
          );
        }
      } catch (error) {
        if (!isProtectedPlanningGenerationCurrent(generation)) throw error;
        const message = error instanceof Error ? error.message : 'The change could not be saved.';
        const earlierOutcomeUnknown = useAtlasDraftSaveRecoveryStore.getState().attempts[requestIntentId]?.outcomeUnknown === true;
        conflictDetected = isSuccessionDraftConflict(message) && !earlierOutcomeUnknown;
        if (isSuccessionAuthorizationDenial(message) && !earlierOutcomeUnknown) {
          useSuccessionOptimisticSlateStore.getState().restore(draftContext, targetRef, revision, rollbackSlate);
        } else {
          useSuccessionOptimisticSlateStore.getState().markUnsaved(
            draftContext,
            targetRef,
            revision,
            message,
          );
          if (isSuccessionDraftConflict(message)) {
            void invalidateSuccessionDraftQueries(queryClient, folderId, diagramId).catch(() => undefined);
          }
        }
        // The command layer only logs, so a swallowed 403/409 reads as "the
        // edit saved" until the panel remounts. Say what the server said.
        toast.error('Succession plan not saved', {
          description: isSuccessionAuthorizationDenial(message)
            ? message
            : isSuccessionDraftConflict(message)
              ? 'The plan changed. Your attempted values remain visible but are not saved. Review the current plan before making a new choice.'
              : `${message} Your latest value is still visible but is not saved. Retry the selection or revert it.`,
        });
        throw error;
      }
    };
    const reverse = (action: 'undo' | 'redo') => {
      const attemptId = `history:${intentId}:${action}`;
      if (!isOrgPlanningReviewScopeCurrent(historyScope)
        || (!historyAttempt?.request && (useOrgPlanningScenarioStore.getState().editEpochByDiagram[diagramId] ?? 0) !== editEpoch)) {
        throw new Error('The selected plan changed. Return to the original plan before undoing or redoing this edit.');
      }
      assertAtlasDraftHistoryAllowed(historyScope, attemptId);
      if (!forwardHead || (action === 'undo' ? undone : !undone)) return Promise.resolve();
      const slate = action === 'undo' ? previousSlate : nextSlate;
      if (historyAttempt && historyAttempt.action !== action) throw new Error('Resolve the unconfirmed history change before continuing.');
      if (!historyAttempt) {
        historyAttempt = { action, revision: stageSlate(slate) };
      }
      const attempt = historyAttempt;
      return enqueueSuccessionTransition(commandContext, async (signal) => {
        assertAtlasDraftHistoryAllowed(historyScope, attemptId);
        const selection = useOrgPlanningScenarioStore.getState();
        if (!isOrgPlanningReviewScopeCurrent(historyScope)
          || (!attempt.request && (selection.editEpochByDiagram[diagramId] ?? 0) !== editEpoch)) throw new Error('The selected plan changed.');
        const selected = selection.selectedByDiagram[diagramId];
        if (!attempt.request) {
          if (draftHistoryEquivalent(historyScope, editEpoch, selected?.headChangeSetId)
            !== draftHistoryEquivalent(historyScope, editEpoch, lastAcceptedHead)
            || (selected && selected.scenarioId !== acceptedScenarioId)) {
            throw new Error('The plan changed since this edit. Review its current values before undoing or redoing it.');
          }
          const sourceHead = selected?.headChangeSetId ?? emptiedHead;
          if (!sourceHead || !acceptedScenarioId) throw new Error('No acknowledged draft revision is available.');
          attempt.request = { sourceHead, scenarioId: acceptedScenarioId, predecessor: selected?.headChangeSetId,
            restoreHead: action === 'undo' ? forwardBaseHead : forwardHead };
        }
        // The same source/snapshot pair is the server's stable retry identity.
        // Restore the complete immutable intent, never infer equivalence from
        // replaying one old slate against a newly discovered head.
        const request = attempt.request;
        trackAtlasDraftSave(attemptId, historyScope, recoveryCommand, 'saving', undefined, undefined, action);
        const restored = await draftHistory.mutateAsync({ ...request, signal });
        if (!isOrgPlanningReviewScopeCurrent(historyScope)) return;
        if (request.restoreHead) acknowledgeDraftHistoryRestore(historyScope, editEpoch, restored, request.restoreHead);
        else emptiedHead = request.sourceHead;
        lastAcceptedHead = request.restoreHead ? restored : undefined;
        await queryClient.cancelQueries({ queryKey: ['atlas', 'organization-plans', folderId, diagramId] });
        await queryClient.invalidateQueries({ queryKey: ['atlas', 'organization-plans', folderId, diagramId], refetchType: 'none' });
        if (!isOrgPlanningReviewScopeCurrent(historyScope)) return;
        const latestSelection = useOrgPlanningScenarioStore.getState();
        if (latestSelection.selectedByDiagram[diagramId]?.headChangeSetId === request.predecessor) {
          if (request.restoreHead) latestSelection.adopt(diagramId, { scenarioId: request.scenarioId, headChangeSetId: restored }, editEpoch);
          else latestSelection.adoptEmpty(diagramId, editEpoch);
        }
        useSuccessionOptimisticSlateStore.getState().accept(draftContext, targetRef, attempt.revision);
        undone = action === 'undo';
        void invalidateSuccessionDraftQueries(queryClient, folderId, diagramId).catch(() => undefined);
      }, queryClient, { lifecycleKey: `draft-${action}` }).then(() => {
        if (historyAttempt === attempt) historyAttempt = undefined;
        completeAtlasDraftSave(attemptId);
      }).catch((error: unknown) => {
        if (attempt.request) {
          const definite = error instanceof PositionCommandSettlementError && error.code === 'http_error'
            && error.status !== undefined && error.status >= 400 && error.status < 500
            && error.serverCode !== 'intent_in_progress';
          if (definite && !useAtlasDraftSaveRecoveryStore.getState().attempts[attemptId]?.outcomeUnknown) {
            historyAttempt = undefined;
            completeAtlasDraftSave(attemptId);
          } else trackAtlasDraftSave(attemptId, historyScope, recoveryCommand, 'unknown', undefined, undefined, action);
        } else historyAttempt = undefined;
        if (isProtectedPlanningGenerationCurrent(generation)) {
          useSuccessionOptimisticSlateStore.getState().markUnsaved(draftContext, targetRef, attempt.revision,
            error instanceof Error ? error.message : 'Draft history outcome unconfirmed.');
        }
        throw error;
      });
    };
    const discardInput = () => {
      useSuccessionOptimisticSlateStore.getState().clearTarget(draftContext, targetRef);
      acknowledgeFailedOrgPlanningMutation(commandContext, recoveryGroup);
    };
    const port: SuccessionCommandServerPort = {
      historyRecoveryId: intentId,
      execute: async () => {
        trackAtlasDraftSave(intentId, historyScope, recoveryCommand, 'saving', recoveryGroup, discardInput);
        try {
          await persist(nextSlate, previousSlate, forwardRevision, intentId);
          completeAtlasDraftSave(intentId);
        } catch (error) {
          const message = error instanceof Error ? error.message : '';
          const definite = isSuccessionDraftConflict(message) || isSuccessionAuthorizationDenial(message)
            || error instanceof PositionCommandSettlementError && error.code === 'http_error'
              && error.status !== undefined && error.status >= 400 && error.status < 500
              && error.serverCode !== 'intent_in_progress';
          // A replaced queued callback never sent this intent. Its winning
          // latest contribution owns recovery; do not offer the obsolete slate.
          if (!attemptedTargets.has(intentId)) completeAtlasDraftSave(intentId);
          else trackAtlasDraftSave(intentId, historyScope, recoveryCommand, definite ? 'failed' : 'unknown', recoveryGroup, discardInput);
          throw error;
        }
      },
      undo: () => reverse('undo'),
      redo: () => reverse('redo'),
    };
    const recoveryCommand: ICommand = { id: intentId, name: 'Save succession draft edit', timestamp: Date.now(),
      historyRecoveryId: intentId,
      mutationDestination: 'protected_plan', execute: port.execute, undo: port.undo, redo: port.redo };
    return port;
  }, [
    diagramId,
    folderId,
    isServerBacked,
    commandContext,
    projection.edges,
    projection.nodes,
    query.data,
    queryClient,
    candidateRefById,
    isLiveProjection,
    save,
    draftHistory,
    sharedScenarioProjection.mode,
  ]);

  const createDraftSlate = useCallback((
    params: Omit<UpdateSuccessionPlanParams, 'access' | 'serverPort'>,
  ): SuccessionSlateFact | undefined => {
    if (!isServerBacked) return undefined;
    const targetRef = resolveSlateTargetRef(params, projection.nodes, projection.edges, diagramId, query.data?.nominations);
    return targetRef
      ? slateFromParams(params, targetRef, projection.nodes, diagramId, candidateRefById)
      : undefined;
  }, [candidateRefById, diagramId, isServerBacked, projection.edges, projection.nodes, query.data?.nominations]);

  const createTransitionPort = useCallback((kind: 'apply' | 'discard'): SuccessionCommandServerPort | undefined => {
    const initialChangeSetId = query.data?.changeSet?.id;
    const optimisticDraftExists = Object.values(optimisticSlates ?? {})
      .some((slate) => slate.phase === 'saving' || slate.phase === 'accepted');
    if (!isServerBacked || (!initialChangeSetId && !optimisticDraftExists)) return undefined;
    let changeSetId = initialChangeSetId ?? '';
    let started = false;
    // Apply runs a stack of server guards (policy version, graph version,
    // source version, approval, post-state). The command layer only logs, so
    // any of them reads as "Apply did nothing" unless the reason is shown.
    const run = async (action: 'apply' | 'discard' | 'revert' | 'restore', signal?: AbortSignal) => {
      try {
        if (successionOptimisticDraftContext(folderId, diagramId) !== optimisticContext) {
          throw new Error('The selected plan changed. Reopen its review before continuing.');
        }
        if (action === 'apply' || action === 'discard') {
          // The immutable head projection may still be loading when Apply is
          // enabled from the accepted mutation response. Stop that request
          // before closing the head so its stale `proposed` payload cannot
          // land after the transition and resurrect an already-settled draft.
          await cancelSuccessionDraftProjection(queryClient, folderId, diagramId);
        }
        const result = await transition.mutateAsync({ changeSetId, action, signal });
        draftHeadRef.current = { awaitingProjection: false };
        // The mutation response is the authoritative lifecycle boundary. Make
        // that accepted state visible locally before the broad reconciliation
        // refetch, otherwise a 200–400 ms discard can leave the closed overlay
        // on screen for many seconds while unrelated Atlas queries settle.
        if (action === 'apply' || action === 'discard') {
          settleSuccessionDraftLocally(queryClient, folderId, diagramId, [], changeSetId);
        }
        registerOrgPlanningCommandBarrier({
          context: commandContext,
          queryClient,
          folderId,
          graphVersion: result.graphVersion,
          onDelayed: () => toast.info('Committed; verifying latest organization…'),
        });
        return result;
      } catch (error) {
        toast.error(`Could not ${action} this succession scenario`, {
          description: error instanceof Error ? error.message : 'The change could not be completed.',
        });
        throw error;
      }
    };
    return {
      execute: async () => {
        await enqueueSuccessionTransition(commandContext, async (signal) => {
          const latestSlates = useSuccessionOptimisticSlateStore.getState().peek(optimisticContext);
          if (Object.values(latestSlates).some((slate) => slate.phase === 'unsaved')) {
            throw new Error('A draft change is unsaved. Retry or revert it before applying the scenario.');
          }
          if (!started) {
            const acceptedHead = draftHeadRef.current.changeSetId
              ?? useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId]?.headChangeSetId;
            if (acceptedHead) changeSetId = acceptedHead;
            if (!changeSetId) throw new Error('The succession draft has not been accepted yet.');
            started = true;
          }
          const result = await run(kind, signal);
          changeSetId = result.changeSetId;
        }, queryClient, {
          lifecycleKey: kind,
          allowFailedPrerequisites: kind === 'discard',
        });
      },
      undo: async () => {
        await enqueueSuccessionTransition(commandContext, async (signal) => {
          const action = kind === 'apply' ? 'revert' : 'restore';
          const result = await run(action, signal);
          // Revert materializes and applies a new inverse change set. Keep that
          // accepted head so redo can invert it again; the original change set
          // remains applied_local and is therefore not restorable.
          changeSetId = result.changeSetId;
        }, queryClient, { lifecycleKey: `undo:${kind}` });
      },
      redo: async () => {
        await enqueueSuccessionTransition(commandContext, async (signal) => {
          const action = kind === 'apply' ? 'revert' : kind;
          const result = await run(action, signal);
          changeSetId = result.changeSetId;
        }, queryClient, { lifecycleKey: `redo:${kind}` });
      },
    };
  }, [commandContext, diagramId, folderId, isServerBacked, optimisticContext, optimisticSlates, query.data?.changeSet?.id, queryClient, transition]);

  const mutationState = summarizeSuccessionMutationState(optimisticSlates);
  const conflictCurrentSlates = useMemo(() => {
    if (!query.data || query.isError || query.isFetching || query.isPlaceholderData
      || query.data.changeSet?.id !== selectedScenario?.headChangeSetId) return undefined;
    const current = new Map<string, CanonicalSuccessionSlate>();
    for (const [targetRef, retained] of Object.entries(optimisticSlates ?? {})) {
      if (retained.phase === 'unsaved' && isSuccessionDraftConflict(retained.error)) {
        current.set(stripRecordReference(targetRef), canonicalSlateFromFact(slateFromProjection(query.data, targetRef)));
      }
    }
    return current;
  }, [optimisticSlates, query.data, query.isError, query.isFetching, query.isPlaceholderData, selectedScenario?.headChangeSetId]);
  const optimisticDraftActive = mutationState.phase === 'saving' || mutationState.phase === 'saved';
  const optimisticChangeCount = Object.keys(optimisticSlates ?? {}).length;

  return {
    ...projection,
    query,
    sharedScenarioProjection,
    isServerBacked,
    // Succession targets resolve through `occupies`, and OrgModelReadService
    // drops *every* occupancy relation when the set is redacted or paged short.
    // Without this the panel cannot tell "nobody holds a position" from "you
    // aren't allowed to see who does" and shows the wrong remedy for both.
    occupancyVisibility: (sharedScenarioProjection.data?.model ?? orgModel.data)?.occupancyVisibility ?? null,
    serverDraftActive: Boolean(query.data?.changeSet && query.data.changeSet.status === 'proposed')
      || optimisticDraftActive,
    // changeCount covers the whole coalesced draft (slates AND staffing
    // gestures); targetCount is the pre-changeCount server's slate-only figure.
    serverChangeCount: query.data?.changeSet?.changeCount
      ?? query.data?.changeSet?.targetCount
      ?? optimisticChangeCount,
    mutationState,
    conflictCurrentSlates,
    candidatePeople,
    canonicalRiskByRecordId,
    canonicalSlates,
    transitionPending: transition.isPending,
    transitionError: transition.isError,
    revertUnsaved: (targetRef: string) => {
      const scope = captureOrgPlanningReviewScope(folderId, diagramId);
      const pending = Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).filter((attempt) =>
        attempt.groupKey === `${optimisticContext}|${targetRef}` && sameOrgPlanningReviewScope(attempt.scope, scope));
      if (pending.some((attempt) => attempt.phase !== 'failed')) {
        toast.error('Confirm the original save outcome before discarding this input.');
        return;
      }
      for (const attempt of pending) abandonAtlasDraftSave(attempt.id);
      useSuccessionOptimisticSlateStore.getState().clearTarget(optimisticContext, targetRef);
      acknowledgeFailedOrgPlanningMutation(commandContext, `${optimisticContext}|${targetRef}`);
    },
    createDraftPort,
    createDraftSlate,
    createApplyPort: () => createTransitionPort('apply'),
    createDiscardPort: () => createTransitionPort('discard'),
  };
}

function projectionWithAcceptedDraft(
  current: SuccessionPlanningProjection | undefined,
  result: { changeSetId: string; scenarioId: string },
  slate: SuccessionSlateFact,
): SuccessionPlanningProjection | undefined {
  if (!current) return undefined;
  const targetRefs = new Set(current.summaries.map((summary) => summary.targetRef));
  targetRefs.add(slate.targetRef);
  return {
    ...current,
    changeSet: {
      id: result.changeSetId,
      scenarioId: result.scenarioId,
      headChangeSetId: result.changeSetId,
      status: 'proposed',
      updatedAt: new Date().toISOString(),
      targetCount: Math.max(current.changeSet?.targetCount ?? 0, targetRefs.size),
      changeCount: Math.max(current.changeSet?.changeCount ?? 0, 1),
    },
  };
}

export function projectSuccessionPlanningOntoCanvas(
  nodes: Node[],
  edges: Edge[],
  diagramId: string,
  projection: SuccessionPlanningProjection | undefined,
  serverBacked: boolean,
  optimisticSlates: ReadonlyMap<string, SuccessionSlateFact> = new Map(),
  /**
   * Canvas node id of each Position record's occupant. Person org charts render
   * people only, so a nomination targeting a durable Position record resolves
   * to no canvas node and its connector would be dropped. Falling back to the
   * occupant draws the successor against the person holding the role — the
   * panel reaches the same Position through virtual nodes, which must never be
   * given to canvas consumers.
   */
  occupantNodeIdByRef: ReadonlyMap<string, string> = new Map(),
  /** Effective source/assertion value from the permissioned shared org model. */
  canonicalKeyPositionByRef: ReadonlyMap<string, boolean> = new Map(),
): { nodes: Node[]; edges: Edge[] } {
  if (!serverBacked) return { nodes, edges };
  const baseEdges = edges.filter((edge) => !isSuccessionEdge(edge));
  const nodeIdByRef = new Map<string, string>();
  for (const node of nodes) {
    const ref = nodeRef(node, diagramId);
    if (ref) nodeIdByRef.set(ref, node.id);
  }
  // A real canvas node for the Position always wins; the occupant is only a
  // fallback for charts that do not render Positions at all.
  const resolveNodeId = (ref: string): string | undefined => {
    for (const alias of recordReferenceAliases(ref)) {
      const nodeId = nodeIdByRef.get(alias) ?? occupantNodeIdByRef.get(alias);
      if (nodeId) return nodeId;
    }
    return undefined;
  };
  const summaryByNodeId = new Map((projection?.summaries ?? []).flatMap((summary) => {
    const nodeId = resolveNodeId(summary.targetRef);
    return nodeId ? [[nodeId, summary] as const] : [];
  }));
  const optimisticByNodeId = new Map(Array.from(optimisticSlates).flatMap(([targetRef, slate]) => {
    const nodeId = resolveNodeId(targetRef);
    return nodeId ? [[nodeId, slate] as const] : [];
  }));
  const canonicalKeyPositionByNodeId = new Map(Array.from(canonicalKeyPositionByRef).flatMap(
    ([targetRef, keyPosition]) => {
      const nodeId = resolveNodeId(targetRef);
      return nodeId ? [[nodeId, keyPosition] as const] : [];
    },
  ));
  const projectedNodes = nodes.map((node) => {
    const summary = summaryByNodeId.get(node.id);
    const optimistic = optimisticByNodeId.get(node.id);
    const canonicalKeyPosition = canonicalKeyPositionByNodeId.get(node.id);
    // On a server-backed chart the canonical nominations below are the only
    // succession truth. A raw `readyNowSuccessor` import hint left on the node
    // would re-derive a view-only "future role" edge with no record behind it —
    // undeletable by construction, and respawning after every slate write
    // (QA issue 73). Imports promote the hint to a real successor_of
    // relationship, so suppressing the field here loses nothing. The derive
    // path reads attrs ?? props ?? top-level, so the check (and the blanking)
    // must cover the same three homes or a props-resident hint slips through.
    const importedHint = node.attrs?.readyNowSuccessor
      ?? node.props?.readyNowSuccessor
      ?? (node as unknown as Record<string, unknown>).readyNowSuccessor;
    const hasImportedHint = typeof importedHint === 'string' && importedHint.trim() !== '';
    if (!summary && !optimistic && canonicalKeyPosition === undefined && !hasImportedHint) return node;
    return {
      ...node,
      attrs: {
        ...node.attrs,
        ...(canonicalKeyPosition !== undefined ? { keyPosition: canonicalKeyPosition } : {}),
        ...(summary ? {
          keyPosition: summary.keyPosition,
          successorCount: summary.successorCount,
          benchStrength: summary.benchStrength,
          successionRisk: summary.successionRisk,
          successionRiskReason: summary.riskReasons.join(' • '),
        } : {}),
        ...(optimistic ? {
          keyPosition: optimistic.keyPosition,
          successorCount: optimistic.candidates.length,
        } : {}),
        readyNowSuccessor: '',
      },
      props: { ...node.props, readyNowSuccessor: '' },
    };
  });
  const optimisticTargetNodeIds = new Set(optimisticByNodeId.keys());
  const projectedEdges: Edge[] = (projection?.nominations ?? []).flatMap((nomination) => {
    const from = resolveNodeId(nomination.personRef);
    const to = resolveNodeId(nomination.targetRef);
    if (!from || !to || from === to || optimisticTargetNodeIds.has(to)) return [];
    return [{
      id: `server-succession:${nomination.recordId}`,
      type: 'successor_for' as Edge['type'],
      from,
      to,
      attrs: {
        relation: 'successor_for',
        predicate: 'successor_of',
        // Server-resolvable nomination identity. The synthetic edge id above
        // is a projection artifact the staffing service cannot match against
        // stored nominations — enact must ship this ref instead.
        relationExternalId: nomination.relationExternalId ?? nomination.recordId,
        readiness: nomination.readiness,
        rank: nomination.rank,
        emergencyCover: nomination.emergencyCover,
        developmentPlan: nomination.developmentPlan,
        criticalExperience: nomination.criticalExperience,
        assessedAt: nomination.assessedAt,
        assessedBy: nomination.assessedBy,
        ...successionConnectorStyleAttrs(),
      },
    }];
  });
  const optimisticEdges: Edge[] = Array.from(optimisticSlates).flatMap(([targetRef, slate]) => {
    const to = resolveNodeId(targetRef);
    if (!to) return [];
    return slate.candidates.flatMap((candidate) => {
      const from = resolveNodeId(candidate.personRef);
      if (!from || from === to) return [];
      return [{
        id: `optimistic-succession:${targetRef}:${candidate.personRef}`,
        type: 'successor_for' as Edge['type'],
        from,
        to,
        attrs: {
          relation: 'successor_for',
          predicate: 'successor_of',
          readiness: candidate.readiness,
          rank: candidate.rank,
          emergencyCover: candidate.emergencyCover,
          developmentPlan: candidate.developmentPlan,
          criticalExperience: candidate.criticalExperience,
          assessedAt: candidate.assessedAt,
          assessedBy: candidate.assessedBy,
          ...successionConnectorStyleAttrs(),
        },
      }];
    });
  });
  return { nodes: projectedNodes, edges: [...baseEdges, ...projectedEdges, ...optimisticEdges] };
}

function nodeRef(node: Node | undefined, diagramId: string): string | null {
  if (!node) return null;
  const recordId = node.attrs?.folderRecordId;
  return typeof recordId === 'string' && recordId.trim()
    ? `record:${recordId}`
    : `node:${diagramId}:${node.id}`;
}

/**
 * The ref a slate write must address, strongest signal first:
 * 1. An explicit role-scoped target (`role:<title>`) — the nomination attaches
 *    to the role itself, no seat resolution involved.
 * 2. The selected node's own record when stored nominations already target it
 *    (imported nominations may target person records directly; a write
 *    addressed anywhere else can never match them for removal).
 * 3. The resolved Position — the default seat-level slate.
 */
function resolveSlateTargetRef(
  params: Pick<UpdateSuccessionPlanParams, 'selectedNodeId' | 'targetRoleRef'>,
  nodes: Node[],
  edges: Edge[],
  diagramId: string,
  nominations: readonly SuccessionProjectionNomination[] | undefined,
): string | null {
  if (params.targetRoleRef?.trim()) return params.targetRoleRef.trim();
  const target = resolveSuccessionTarget(params.selectedNodeId, nodes, edges);
  const selectedRef = nodeRef(nodes.find((node) => node.id === params.selectedNodeId), diagramId);
  const nominationTargetAliases = new Set((nominations ?? [])
    .flatMap((nomination) => recordReferenceAliases(nomination.targetRef)));
  const selectedOwnsNominations = Boolean(selectedRef)
    && recordReferenceAliases(selectedRef!).some((alias) => nominationTargetAliases.has(alias));
  if (selectedOwnsNominations) return selectedRef;
  if (target.kind !== 'position') return null;
  return nodeRef(nodes.find((node) => node.id === target.targetId), diagramId);
}

function slateFromParams(
  params: Omit<UpdateSuccessionPlanParams, 'access' | 'serverPort'>,
  targetRef: string,
  nodes: Node[],
  diagramId: string,
  candidateRefById: ReadonlyMap<string, string>,
): SuccessionSlateFact {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  // The suppression target is the HINT HOLDER's record — the node whose
  // source-authored readyNowSuccessor names the removed candidate — never the
  // slate target. A holder without a folder record has nothing durable to
  // tombstone, so it is dropped rather than mis-addressed.
  const suppressedSourceHints = (params.removedLegacyNominations ?? []).flatMap((entry) => {
    const holder = byId.get(entry.holderNodeId);
    const holderRecordId = holder?.attrs?.folderRecordId ?? holder?.props?.folderRecordId;
    if (typeof holderRecordId !== 'string' || !holderRecordId.trim()) return [];
    const personRef = entry.personId
      ? nodeRef(byId.get(entry.personId), diagramId)
        ?? candidateRefById.get(entry.personId)
        ?? (entry.personId.trim() ? `record:${entry.personId.trim()}` : undefined)
      : undefined;
    return [{
      targetRef: `record:${holderRecordId.trim()}`,
      ...(personRef ? { personRef } : {}),
    }];
  });
  return {
    targetRef,
    keyPosition: params.keyPosition,
    ...(suppressedSourceHints.length ? { suppressedSourceHints } : {}),
    candidates: params.candidates.flatMap((candidate) => {
      // Canonical slate candidates carry folder-record ids rather than canvas
      // node ids; when neither the canvas nor the candidate directory knows
      // the id, address the record directly instead of dropping the entry.
      const personRef = nodeRef(byId.get(candidate.personId), diagramId)
        ?? candidateRefById.get(candidate.personId)
        ?? (candidate.personId.trim() ? `record:${candidate.personId}` : null);
      return personRef ? [{
        personRef,
        readiness: candidate.readiness,
        rank: candidate.rank,
        emergencyCover: candidate.emergencyCover,
        developmentPlan: candidate.developmentPlan,
        criticalExperience: candidate.criticalExperience,
        assessedAt: candidate.assessedAt,
        assessedBy: candidate.assessedBy,
      }] : [];
    }),
  };
}

function canonicalSlateFromFact(slate: SuccessionSlateFact): CanonicalSuccessionSlate {
  return {
    keyPosition: slate.keyPosition,
    candidates: slate.candidates.map((candidate): SuccessionCandidate => ({
      edgeId: candidate.relationExternalId ?? '',
      personId: stripRecordReference(candidate.personRef),
      targetId: stripRecordReference(slate.targetRef),
      readiness: candidate.readiness,
      rank: candidate.rank,
      emergencyCover: candidate.emergencyCover,
      developmentPlan: candidate.developmentPlan,
      criticalExperience: candidate.criticalExperience,
      assessedAt: candidate.assessedAt,
      assessedBy: candidate.assessedBy,
      source: 'relationship',
    })),
  };
}


function slateFromProjection(
  projection: SuccessionPlanningProjection | undefined,
  targetRef: string,
): SuccessionSlateFact {
  const summary = projection?.summaries.find((item) => sameRecordReference(item.targetRef, targetRef));
  return {
    targetRef,
    keyPosition: summary?.keyPosition ?? false,
    candidates: projection?.nominations
      .filter((item) => sameRecordReference(item.targetRef, targetRef))
      .map((item) => ({ ...item })) ?? [],
  };
}

function sameSuccessionSlate(left: SuccessionSlateFact, right: SuccessionSlateFact): boolean {
  if (!sameRecordReference(left.targetRef, right.targetRef) || left.keyPosition !== right.keyPosition) return false;
  if (left.candidates.length !== right.candidates.length) return false;
  const byPersonRef = (slate: SuccessionSlateFact) => [...slate.candidates]
    .sort((a, b) => stripRecordReference(a.personRef).localeCompare(stripRecordReference(b.personRef)));
  const leftCandidates = byPersonRef(left);
  const rightCandidates = byPersonRef(right);
  return leftCandidates.every((candidate, index) => {
    const other = rightCandidates[index];
    return sameRecordReference(candidate.personRef, other.personRef)
      && candidate.readiness === other.readiness
      && candidate.rank === other.rank
      && candidate.emergencyCover === other.emergencyCover
      && candidate.developmentPlan === other.developmentPlan
      && candidate.criticalExperience === other.criticalExperience
      && candidate.assessedAt === other.assessedAt
      && candidate.assessedBy === other.assessedBy;
  });
}

function sameRecordReference(left: string, right: string): boolean {
  return stripRecordReference(left) === stripRecordReference(right);
}

function stripRecordReference(value: string): string {
  return value.startsWith('record:') ? value.slice('record:'.length) : value;
}

function readProjectedTitle(props: Readonly<Record<string, unknown>>): string | undefined {
  const value = props.title ?? props.role;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function recordReferenceAliases(value: string): readonly string[] {
  const canonical = stripRecordReference(value);
  return value === canonical ? [value, `record:${canonical}`] : [value, canonical];
}

/**
 * Draft amendments only replace the succession projection head. Keep the
 * scenario list stale for its next read, but do not rebuild the canonical org
 * model or cross-domain projections until Apply/Revert/Discard.
 */
export async function invalidateSuccessionDraftQueries(
  queryClient: QueryClient,
  folderId: string,
  diagramId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['atlas-succession', folderId, diagramId],
      refetchType: 'none',
    }),
    queryClient.invalidateQueries({
      queryKey: ['atlas', 'organization-plans', folderId],
      refetchType: 'none',
    }),
  ]);
}

/** Stops a draft-head read before its lifecycle transition can close that head. */
export function cancelSuccessionDraftProjection(
  queryClient: QueryClient,
  folderId: string,
  diagramId: string,
): Promise<void> {
  return queryClient.cancelQueries(
    { queryKey: ['atlas-succession', folderId, diagramId] },
    { silent: true },
  );
}

/**
 * Closes every client representation of one diagram's accepted server draft.
 * Both command-backed panel actions and the always-visible mode-chip actions
 * must cross this same local lifecycle boundary.
 */
export function settleSuccessionDraftLocally(
  queryClient: QueryClient,
  folderId: string,
  diagramId: string,
  spentTargetRefs: readonly string[] = [],
  settledHeadChangeSetId?: string,
  keyPositionPatches: readonly ConfirmedKeyPositionPatch[] = [],
): void {
  const selected = useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId];
  const settledHead = settledHeadChangeSetId ?? selected?.headChangeSetId;
  if (!settledHead) return;
  const settledScenario = selected?.headChangeSetId === settledHead ? selected?.scenarioId : undefined;
  if (!selected || selected.headChangeSetId === settledHead) {
    useSuccessionOptimisticSlateStore.getState().clear(successionOptimisticDraftContext(folderId, diagramId));
  }
  const spentTargets = new Set(spentTargetRefs.map(stripRecordReference));
  const confirmedKeyPositions = new Map(keyPositionPatches.map((patch) =>
    [stripRecordReference(patch.targetRef), patch.keyPosition]));
  queryClient.setQueriesData<SuccessionPlanningProjection>(
    { queryKey: ['atlas-succession', folderId, diagramId] },
    (current) => {
      if (!current) return current;
      const settledDraft = current.changeSet?.id === settledHead
        || Boolean(settledScenario && current.changeSet?.scenarioId === settledScenario);
      // This cache becomes active immediately after the submitted draft closes.
      // Patch it only when the confirmed receipt contains a post-state change.
      const liveProjection = current.changeSet === null
        && (spentTargets.size > 0 || confirmedKeyPositions.size > 0);
      if (!settledDraft && !liveProjection) return current;
      if (spentTargets.size === 0) {
        return {
          ...current,
          changeSet: null,
          summaries: applyConfirmedKeyPositionSummaries(
            current.summaries,
            current.nominations,
            confirmedKeyPositions,
          ),
        };
      }
      const nominations = current.nominations.filter((nomination) =>
        !spentTargets.has(stripRecordReference(nomination.targetRef)),
      );
      const summaries = current.summaries.map((summary) => {
        if (!spentTargets.has(stripRecordReference(summary.targetRef))) return summary;
        return {
          ...summary,
          successorCount: 0,
          readyNowPersonRefs: [],
          benchStrength: 'none' as const,
          coverageRisk: 'critical' as const,
          successionRisk: 'critical' as const,
          riskReasons: [
            'No named successor',
            ...(summary.keyPosition ? ['Key position'] : []),
          ],
        };
      });
      return {
        ...current,
        changeSet: null,
        nominations,
        summaries: applyConfirmedKeyPositionSummaries(summaries, nominations, confirmedKeyPositions),
      };
    },
  );
  if (selected?.headChangeSetId === settledHead) {
    const planning = useOrgPlanningScenarioStore.getState();
    planning.select(diagramId, null);
    planning.setMode(diagramId, 'live');
  }
  queryClient.removeQueries({
    predicate: (candidate) => {
      const [scope, queryFolderId, queryDiagramId, scenarioId, headChangeSetId] = candidate.queryKey;
      return scope === 'atlas-succession'
        && queryFolderId === folderId
        && queryDiagramId === diagramId
        && Boolean(scenarioId || headChangeSetId)
        && (settledScenario ? scenarioId === settledScenario : headChangeSetId === settledHead);
    },
  });
}

function applyConfirmedKeyPositionSummaries(
  summaries: readonly SuccessionProjectionSummary[],
  nominations: readonly SuccessionProjectionNomination[],
  confirmedKeyPositions: ReadonlyMap<string, boolean>,
): readonly SuccessionProjectionSummary[] {
  if (confirmedKeyPositions.size === 0) return summaries;
  const existingTargets = new Set(summaries.map((summary) =>
    stripRecordReference(summary.targetRef)));
  const nominationCountByTarget = new Map<string, number>();
  for (const nomination of nominations) {
    const target = stripRecordReference(nomination.targetRef);
    nominationCountByTarget.set(target, (nominationCountByTarget.get(target) ?? 0) + 1);
  }
  const next = summaries.map((summary) => {
    const keyPosition = confirmedKeyPositions.get(stripRecordReference(summary.targetRef));
    if (typeof keyPosition !== 'boolean') return summary;
    return {
      ...summary,
      keyPosition,
      riskReasons: keyPosition
        ? [...new Set([...summary.riskReasons, 'Key position'])]
        : summary.riskReasons.filter((reason) => reason !== 'Key position'),
    };
  });
  for (const [target, keyPosition] of confirmedKeyPositions) {
    if (existingTargets.has(target)) continue;
    next.push({
      targetRef: `record:${target}`,
      keyPosition,
      successorCount: nominationCountByTarget.get(target) ?? 0,
      readyNowPersonRefs: [],
      benchStrength: 'none',
      coverageRisk: 'critical',
      successionRisk: 'critical',
      riskReasons: ['No named successor', ...(keyPosition ? ['Key position'] : [])],
    });
  }
  return next;
}

function isSuccessionEdge(edge: Edge): boolean {
  const relation = String(edge.attrs?.predicate ?? edge.attrs?.relation ?? edge.type);
  return relation === 'successor_for' || relation === 'successor_of';
}

function successionSaveTarget(
  changeSetId: string | undefined,
  scenarioId: string | undefined,
): SuccessionSaveTarget {
  if (changeSetId && scenarioId) {
    return {
      draftMode: 'amend',
      changeSetId,
      scenarioId,
      expectedHeadChangeSetId: changeSetId,
    };
  }
  if (changeSetId || scenarioId) {
    throw new Error('The succession draft identity is incomplete. Refresh before retrying.');
  }
  return { draftMode: 'create' };
}

async function apiError(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => ({})) as { error?: string };
  return body.error ?? fallback;
}

function waitForSuccessionIntent(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(signal.reason);
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      globalThis.clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = globalThis.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function isSuccessionAuthorizationDenial(message: string): boolean {
  return message === 'authorization_denied'
    || message === 'folder_access_denied'
    || message === 'Forbidden'
    || message.startsWith('data_grant_')
    || message.startsWith('entitlement_')
    || message.startsWith('folder_role_')
    || message.startsWith('record_scope_');
}

function summarizeSuccessionMutationState(
  slates: Readonly<Record<string, { readonly phase: 'saving' | 'accepted' | 'unsaved'; readonly error?: string }>> | undefined,
): SuccessionDraftMutationState {
  const pending = Object.values(slates ?? {});
  const unsaved = pending.find((item) => item.phase === 'unsaved');
  if (unsaved) return { phase: 'unsaved', ...(unsaved.error ? { error: unsaved.error } : {}),
    conflictedTargetRefs: Object.entries(slates ?? {})
      .filter(([, item]) => item.phase === 'unsaved' && isSuccessionDraftConflict(item.error)).map(([targetRef]) => targetRef),
  };
  if (pending.some((item) => item.phase === 'saving')) return { phase: 'saving' };
  if (pending.some((item) => item.phase === 'accepted')) return { phase: 'saved' };
  return { phase: 'idle' };
}
